import { gql } from '@apollo/client'
// graphql
import { FIELD_FRAGMENT } from 'graphql/field/queries'
import { PLAN_KPI_REVIEWS_FRAGMENT } from 'graphql/planKPIs/queries.js'
import { PLAN_ACTION_REVIEWS_FRAGMENT } from 'graphql/planActions/queries.js'
import { PLAN_OBJECTIVE_REVIEWS_FRAGMENT } from 'graphql/planObjectives/queries.js'

// fragments
export const PLAN_DETAILS_FRAGMENT = gql`
  fragment PLAN_DETAILS on Plan {
    id
    companyId
    parentPlanId
    name
    status
    private
    locked
    isBookmark
    ownedById
    ownedByUser {
      id
      firstName
      lastName
    }
    ownedByPosition {
      id
      description
      user {
        id
        firstName
        lastName
      }
    }
    businessActivities {
      id
      description
    }
    businessUnits {
      id
      description
    }
    businessProcesses {
      id
      description
    }
    memberPositions {
      id
      description
    }
    memberUsers {
      id
      firstName
      lastName
    }
    planType {
      id
      description
      levels {
        id
        color
        position
        description
        typeOptions {
          id
          label
        }
      }
      fields {
        ...FIELD
      }
      objectiveOwnerObjectSecurityId
      objectiveMemberObjectSecurityId
      actionOwnerObjectSecurityId
      actionMemberObjectSecurityId
      kpiOwnerObjectSecurityId
      kpiMemberObjectSecurityId
      planOwnerObjectSecurityId
      planMemberObjectSecurityId
    }
    fieldValueMap {
      fieldName
      fieldValue
      fieldType
      entityId
    }
    columnSettings
    scheduleDefaults {
      entityType
      levelId
      repeating
      repeatEveryValue
      repeatEveryType
      periodEndsOn
      startPeriod
      endPeriod
      endsOn
      endsOnDate
      reviewDate {
        date
        end
        value
        period
      }
    }
  }
  ${FIELD_FRAGMENT}
`

export const PLAN_SUB_PLANS_FRAGMENT = gql`
  fragment PLAN_SUB_PLANS on Plan {
    subPlans {
      id
      name
      private
      companyId
      parentPlan {
        id
      }
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
      }
      memberPositions {
        id
        description
      }
      memberUsers {
        id
        firstName
        lastName
      }
      planType {
        id
        description
        levels {
          id
          color
          position
          companyId
          description
        }
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
    }
  }
`

export const PLAN_PARENT_PLAN_FRAGMENT = gql`
  fragment PLAN_PARENT_PLAN on Plan {
    parentPlan {
      id
      name
      private
      companyId
      parentPlan {
        id
      }
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
      }
      memberPositions {
        id
        description
      }
      memberUsers {
        id
        firstName
        lastName
      }
      planType {
        id
        description
        levels {
          id
          color
          position
          companyId
          description
        }
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
    }
  }
`

export const PLAN_LINKED_PLANS_FRAGMENT = gql`
  fragment PLAN_LINKED_PLANS on Plan {
    linkedPlans {
      id
      name
      private
      companyId
      parentPlan {
        id
      }
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
      }
      planType {
        id
        description
        levels {
          id
          color
          position
          companyId
          description
        }
      }
      memberPositions {
        id
        description
      }
      memberUsers {
        id
        firstName
        lastName
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
    }
  }
`

export const PLAN_REVIEWS_FRAGMENT = gql`
  fragment PLAN_REVIEWS on Plan {
    scheduledReviews {
      id
      planId
      companyId
      repeating
      repeatEveryValue
      repeatEveryType
      periodEndsOn
      startPeriod
      endPeriod
      scheduledReviewers {
        id
        userId
        positionId
        scheduledReviewId
        reviewDate {
          end
          value
          date
          period
        }
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        position {
          id
          description
        }
      }
      endsOn
      endsOnDate
    }
    reviews {
      id
      planId
      createdAt
      updatedAt
      startPeriod
      endPeriodAt
      dueAt
      scheduledReviewId
      scheduledReview {
        id
        planId
        companyId
        repeating
        repeatEveryValue
        repeatEveryType
        periodEndsOn
        startPeriod
        endPeriod
        scheduledReviewers {
          id
          userId
          positionId
          scheduledReviewId
          reviewDate {
            end
            value
            date
            period
          }
          user {
            id
            firstName
            lastName
            avatarUrl
          }
          position {
            id
            description
          }
        }
        endsOn
        endsOnDate
      }
      status
      values {
        goals
        doWell
        doBetter
      }
      fieldValueMap {
        fieldId
        fieldValueId
        fieldValue
        fieldLabel
      }
      updatedBy {
        id
        firstName
        lastName
      }
    }
  }
`

export const PLAN_ITEMS_FRAGMENT = gql`
  fragment PLAN_ITEMS on PlanItem {
    planKPI {
      id
      archived
      description
      recurrence
      resultTarget
      resultType
      resultTypeScale {
        id
        active
        position
        label
        value
      }
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
        avatarUrl
      }
      planItems {
        data {
          id
          referenced
          path
          plan {
            id
            name
          }
        }
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
      destinationLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      sourceLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      ...PLAN_KPI_REVIEWS
    }
    planAction {
      id
      archived
      description
      commencementAt @include(if: $withCommencementAt)
      completionAt
      completionPercentage
      actualCompletionDate @include(if: $withActualCompletionDate)
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
        avatarUrl
      }
      planItems {
        data {
          id
          referenced
          path
          plan {
            id
            name
          }
        }
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
      destinationLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      sourceLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      ...PLAN_ACTION_REVIEWS
    }
    planObjective {
      id
      archived
      color
      description
      commencementAt @include(if: $withCommencementAt)
      completionAt
      completionPercentage
      actualCompletionDate @include(if: $withActualCompletionDate)
      ownedById
      ownedByPosition {
        id
        description
      }
      ownedByUser {
        id
        firstName
        lastName
        avatarUrl
      }
      planItems {
        data {
          id
          referenced
          path
          plan {
            id
            name
          }
        }
      }
      fieldValueMap {
        fieldName
        fieldValue
      }
      destinationLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      sourceLinkingItems {
        id
        sourcePlanKPIId
        destinationPlanKPIId
        sourcePlanActionId
        destinationPlanActionId
        sourcePlanObjectiveId
        destinationPlanObjectiveId
      }
      ...PLAN_OBJECTIVE_REVIEWS
    }
  }
  ${PLAN_ACTION_REVIEWS_FRAGMENT}
  ${PLAN_KPI_REVIEWS_FRAGMENT}
  ${PLAN_OBJECTIVE_REVIEWS_FRAGMENT}
`

export const PLAN_PERMISSIONS_FRAGMENT = gql`
  fragment PLAN_PERMISSIONS on Plan {
    currentPermissions {
      planModifyPlan
      planDeletePlan
      planCreateScheduledReview
      planModifyScheduledReview
      planDeleteScheduledReview
      planViewInProgressReview
      planViewCompletedReview
      planLinkObjects
      planAddItemsFromParentPlan
      planAddObjective
      planAddKPI
      planAddAction
      planSetPlanSecurity
      planSetUserPlanSecurity
      planCreateSubPlan
    }
  }
`

// queries
export const OWNED_BY_SELECT_QUERY = gql`
  query OWNED_BY_SELECT_QUERY(
    $userFilter: UserFilter
    $positionFilter: PositionFilterInput
    $order: OrderInput
    $pagination: PaginationInput
  ) {
    users(filter: $userFilter, order: $order, pagination: $pagination) {
      data {
        id
        active
        firstName
        lastName
        companyId
        displayName
      }
      pagination {
        pageSkip
        pageTake
        totalCount
      }
    }
    positions(filter: $positionFilter, pagination: $pagination) {
      data {
        id
        active
        description
        companyId
      }
      pagination {
        pageSkip
        pageTake
        totalCount
      }
    }
  }
`

export const PLANS_LIST_QUERY = gql`
  query PLANS_LIST_QUERY($filter: PlanFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    plans(filter: $filter, order: $order, pagination: $pagination) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        name
        status
        companyId
        parentPlanId
        private
        planType {
          id
          description
          levels {
            id
            color
            position
            companyId
            description
          }
          fields {
            id
            name
            options {
              id
              label
            }
          }
          objectiveOwnerObjectSecurityId
          objectiveMemberObjectSecurityId
          actionOwnerObjectSecurityId
          actionMemberObjectSecurityId
          kpiOwnerObjectSecurityId
          kpiMemberObjectSecurityId
          planOwnerObjectSecurityId
          planMemberObjectSecurityId
        }
        ownedById
        ownedByUser {
          id
          firstName
          lastName
        }
        ownedByPosition {
          id
          description
          user {
            id
            firstName
            lastName
          }
        }
        fieldValueMap {
          entityId
          fieldName
          fieldValue
        }
        ...PLAN_REVIEWS
        ...PLAN_PERMISSIONS
      }
    }
  }
  ${PLAN_REVIEWS_FRAGMENT}
  ${PLAN_PERMISSIONS_FRAGMENT}
`

export const PLANS_SELECT_QUERY = gql`
  query PLANS_SELECT_QUERY($filter: PlanFilterInput, $order: OrderInput, $pagination: PaginationInput) {
    plans(filter: $filter, order: $order, pagination: $pagination) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        name
        companyId
      }
    }
  }
`

export const PLANS_DETAILS_QUERY = gql`
  query PLANS_DETAILS_QUERY($filter: PlanFilterInput) {
    plans(filter: $filter) {
      data {
        ...PLAN_DETAILS
        ...PLAN_LINKED_PLANS
        ...PLAN_PARENT_PLAN
        ...PLAN_PERMISSIONS
        ...PLAN_REVIEWS
        ...PLAN_SUB_PLANS
      }
    }
  }
  ${PLAN_DETAILS_FRAGMENT}
  ${PLAN_LINKED_PLANS_FRAGMENT}
  ${PLAN_PARENT_PLAN_FRAGMENT}
  ${PLAN_PERMISSIONS_FRAGMENT}
  ${PLAN_REVIEWS_FRAGMENT}
  ${PLAN_SUB_PLANS_FRAGMENT}
`

export const PLAN_DETAILS_TREE_QUERY = gql`
  query PLAN_DETAILS_TREE_QUERY(
    $filter: PlanItemFilterInput!
    $withCommencementAt: Boolean! = false
    $withActualCompletionDate: Boolean! = false
    $withResourceBudget: Boolean! = false
    $withResourceCost: Boolean! = false
  ) {
    planItems(filter: $filter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        planId
        parentId
        expanded
        matchesFilter
        treePosition
        referenced
        # Each total is only requested when the plan shows that column
        # (see getPlanColumnVariables in utils/planColumns). The same applies to the item dates
        # requested inside PLAN_ITEMS.
        resourceBudget @include(if: $withResourceBudget)
        resourceCost @include(if: $withResourceCost)
        ...PLAN_ITEMS
      }
    }
  }
  ${PLAN_ITEMS_FRAGMENT}
`

export const PLAN_ITEMS_TREE_POSITIONS_QUERY = gql`
  query PLAN_ITEMS_TREE_POSITIONS_QUERY($filter: PlanItemFilterInput!) {
    planItems(filter: $filter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      data {
        id
        parentId
        expanded
        treePosition
        referenced
      }
    }
  }
`

export const PARENT_PLAN_SHORT_DETAILS_TREE_QUERY = gql`
  query PARENT_PLAN_SHORT_DETAILS_TREE_QUERY($filter: PlanItemFilterInput!) {
    planItems(filter: $filter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        planId
        treePosition
        planKPI {
          id
        }
        planAction {
          id
        }
        planObjective {
          id
        }
      }
    }
  }
`

export const PLANS_TASKS_QUERY = gql`
  query PLANS_TASKS_QUERY {
    homePlans {
      data {
        plans {
          data {
            id
            companyId
            name
            ownedById
            planType {
              id
              levels {
                id
                description
                position
              }
            }
            planItems {
              data {
                id
                planObjective {
                  id
                  description
                  commencementAt
                  completionAt
                  completionPercentage
                  ownedByPosition {
                    id
                    description
                  }
                  ownedByUser {
                    id
                    firstName
                    lastName
                    avatarUrl
                  }
                  currentPermissions {
                    objectiveModifyObjective
                  }
                  ...PLAN_OBJECTIVE_REVIEWS
                }
                planAction {
                  id
                  description
                  commencementAt
                  completionAt
                  completionPercentage
                  ownedByPosition {
                    id
                    description
                  }
                  ownedByUser {
                    id
                    firstName
                    lastName
                    avatarUrl
                  }
                  currentPermissions {
                    actionModifyAction
                  }
                  ...PLAN_ACTION_REVIEWS
                }
                planKPI {
                  id
                  description
                  recurrence
                  resultTarget
                  resultType
                  resultTypeScale {
                    id
                    active
                    position
                    label
                    value
                  }
                  ownedByPosition {
                    id
                    description
                  }
                  ownedByUser {
                    id
                    firstName
                    lastName
                    avatarUrl
                  }
                  currentPermissions {
                    kpiAddKPIResult
                  }
                  ...PLAN_KPI_REVIEWS
                }
                path
              }
            }
            ...PLAN_REVIEWS
          }
        }
        actions {
          id
          description
          commencementAt
          completionAt
          completionPercentage
          ownedByPosition {
            id
            description
          }
          ownedByUser {
            id
            firstName
            lastName
          }
          currentPermissions {
            actionModifyAction
          }
          ...PLAN_ACTION_REVIEWS
        }
        kpis {
          id
          description
          recurrence
          resultTarget
          resultType
          resultTypeScale {
            id
            active
            position
            label
            value
          }
          ownedByPosition {
            id
            description
          }
          ownedByUser {
            id
            firstName
            lastName
          }
          currentPermissions {
            kpiAddKPIResult
          }

          ...PLAN_KPI_REVIEWS
        }
      }
    }
  }
  ${PLAN_ACTION_REVIEWS_FRAGMENT}
  ${PLAN_KPI_REVIEWS_FRAGMENT}
  ${PLAN_OBJECTIVE_REVIEWS_FRAGMENT}
  ${PLAN_REVIEWS_FRAGMENT}
`

export const PLAN_DETAILS_PLAN_ITEMS_QUERY = gql`
  query PLAN_DETAILS_PLAN_ITEMS_QUERY(
    $objectiveFilter: PlanObjectiveFilterInput!
    $actionFilter: PlanActionFilterInput!
    $kpiFilter: PlanKPIFilterInput!
  ) {
    planObjectives(filter: $objectiveFilter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        description
        planItems {
          data {
            id
            planId
          }
        }
      }
    }
    planKPIs(filter: $kpiFilter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        description
        planItems {
          data {
            id
            planId
          }
        }
      }
    }
    planActions(filter: $actionFilter, pagination: { pageTake: 10000, pageSkip: 0 }) {
      data {
        id
        description
        planItems {
          data {
            id
            planId
          }
        }
      }
    }
  }
`

export const PLAN_ACTIVITIES_LIST_QUERY = gql`
  query PLAN_ACTIVITIES_LIST_QUERY($filter: LogFilterInput!) {
    logs(filter: $filter) {
      data {
        id
        actionType
        createdAt
        entityId
        entityType
        metadata {
          fileName
          fileUrl
          fileContentType
          comment
        }
        user {
          id
          firstName
          lastName
          avatarUrl
        }
      }
    }
  }
`
